import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CardMedia,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Delete as RemoveIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import api from '../../../services/api';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  publicBodyFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../caseManagerStyles';

const QuestionsStep = ({ data, onUpdate }) => {
  const [questionDialog, setQuestionDialog] = useState({
    open: false,
    questionIndex: -1,
    question: null
  });
  const [uploadError, setUploadError] = useState('');
  const [keepOriginalResolution, setKeepOriginalResolution] = useState(false);

  const compressImageFile = (file) => new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const maxDimension = 2048;
        const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
        const targetWidth = Math.max(1, Math.round(img.width * scale));
        const targetHeight = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        const targetType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const extension = targetType === 'image/png' ? '.png' : '.jpg';
          const newName = file.name.replace(/\.[^/.]+$/, extension);
          resolve(new File([blob], newName, { type: targetType, lastModified: Date.now() }));
        }, targetType, targetType === 'image/png' ? undefined : 0.82);
      };
      img.onerror = () => resolve(file);
      img.src = event.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

  const createNewQuestion = (order) => ({
    id: `question_${Date.now()}`,
    question: '',
    correctAnswer: '',
    maxMarks: 2,
    markingInstructions: '',
    mediaFiles: [],
    order
  });

  const moveQuestionUp = (questionIndex) => {
    if (questionIndex === 0) return;

    const newQuestions = [...data.examinerQuestions];
    const temp = newQuestions[questionIndex];
    newQuestions[questionIndex] = newQuestions[questionIndex - 1];
    newQuestions[questionIndex - 1] = temp;

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const moveQuestionDown = (questionIndex) => {
    if (questionIndex === data.examinerQuestions.length - 1) return;

    const newQuestions = [...data.examinerQuestions];
    const temp = newQuestions[questionIndex];
    newQuestions[questionIndex] = newQuestions[questionIndex + 1];
    newQuestions[questionIndex + 1] = temp;

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const addQuestion = () => {
    const newQuestion = createNewQuestion(data.examinerQuestions.length);

    setQuestionDialog({
      open: true,
      questionIndex: data.examinerQuestions.length,
      question: { ...newQuestion }
    });
  };

  const editQuestion = (questionIndex) => {
    const question = data.examinerQuestions[questionIndex];
    setQuestionDialog({
      open: true,
      questionIndex,
      question: { ...question }
    });
  };

  const saveQuestion = () => {
    const { questionIndex, question } = questionDialog;

    // Validate required fields
    if (!question.question?.trim() || !question.correctAnswer?.trim() || !question.markingInstructions?.trim()) {
      setUploadError('Question, correct answer, and marking instructions are required');
      return;
    }

    let newQuestions = [...data.examinerQuestions];

    if (questionIndex === data.examinerQuestions.length) {
      // This is a new question, add it to the array
      question.order = newQuestions.length;
      newQuestions.push(question);
    } else {
      // This is an existing question, update it
      newQuestions[questionIndex] = question;
    }

    onUpdate({ examinerQuestions: newQuestions });
    setQuestionDialog({ open: false, questionIndex: -1, question: null });
    setUploadError('');
  };

  const deleteQuestion = (questionIndex) => {
    const newQuestions = data.examinerQuestions.filter((_, index) => index !== questionIndex);

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const handleDialogClose = () => {
    // Don't save anything on cancel, just close
    setQuestionDialog({ open: false, questionIndex: -1, question: null });
    setUploadError('');
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setUploadError('');

    if (files.length === 0) return;

    const validFiles = [];
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`File ${file.name} is not a valid file type. Allowed: images and PDFs`);
        return;
      }
      if (file.size > maxSize) {
        setUploadError(`File ${file.name} is too large (max 50MB)`);
        return;
      }
      validFiles.push(file);
    }

    try {
      const processedFiles = [];
      for (const file of validFiles) {
        if (!keepOriginalResolution && file.type.startsWith('image/')) {
          try {
            const compressed = await compressImageFile(file);
            processedFiles.push(compressed);
          } catch {
            processedFiles.push(file);
          }
        } else {
          processedFiles.push(file);
        }
      }

      // Upload files to backend
      const formData = new FormData();
      processedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Use 'temp' as case ID for new cases, or actual case ID for editing
      const caseId = data.id || 'temp';
      const response = await api.post(`/cases/${caseId}/questions/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedFiles = response.data.files.map(file => ({
        id: uuidv4(),
        filename: file.filename,
        originalName: file.originalName,
        path: file.path,
        url: file.url,
        mimetype: file.mimetype,
        size: file.size,
        uploadedAt: new Date()
      }));

      const updatedQuestion = {
        ...questionDialog.question,
        mediaFiles: [...(questionDialog.question.mediaFiles || []), ...uploadedFiles]
      };

      setQuestionDialog({
        ...questionDialog,
        question: updatedQuestion
      });

    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload files');
    }

    // Clear the input
    event.target.value = '';
  };

  const removeFile = (fileIndex) => {
    const updatedFiles = questionDialog.question.mediaFiles.filter((_, index) => index !== fileIndex);
    setQuestionDialog({
      ...questionDialog,
      question: {
        ...questionDialog.question,
        mediaFiles: updatedFiles
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <CaseEditorStepFrame>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3, fontFamily: publicBodyFontFamily }}>
        Images are compressed automatically to keep downloads light. Keep original resolution only when image fidelity matters for teaching.
      </Typography>

      {/* Option to add questions */}
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={3}>
        <Button
          startIcon={<AddIcon />}
          onClick={addQuestion}
          sx={publicPrimaryButtonSx}
        >
          Add Question
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={keepOriginalResolution}
              onChange={(event) => setKeepOriginalResolution(event.target.checked)}
            />
          }
          label="Keep original resolution for uploaded images"
        />
      </Box>

      {/* Questions List */}
      <Box>
        {data.examinerQuestions.map((question, questionIndex) => (
          <Card key={question.id} sx={{ ...caseManagerInsetSurfaceSx, mb: 2, borderRadius: '22px' }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start">
                <Box flexGrow={1}>
                  <Typography variant="h6" gutterBottom>
                    Question {questionIndex + 1}
                  </Typography>

                  <Typography variant="body1" gutterBottom>
                    {question.question || 'No question text'}
                  </Typography>

                  <Box display="flex" gap={1} mb={1}>
                    <Chip
                      label={`${question.maxMarks} marks`}
                      size="small"
                      color="primary"
                    />
                    {question.mediaFiles && question.mediaFiles.length > 0 && (
                      <Chip
                        label={`${question.mediaFiles.length} media file(s)`}
                        size="small"
                        color="secondary"
                        icon={<ImageIcon />}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Correct Answer:</strong> {question.correctAnswer || 'No answer provided'}
                  </Typography>

                  {question.mediaFiles && question.mediaFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>Media Files:</Typography>
                      <Grid container spacing={1}>
                        {question.mediaFiles.map((file, fileIndex) => (
                          <Grid item key={fileIndex}>
                            <Card sx={{ width: 100, height: 80 }}>
                              <CardMedia
                                component="img"
                                height="60"
                                image={file.url}
                                alt={file.originalName}
                                sx={{ objectFit: 'cover', cursor: 'pointer' }}
                                onClick={() => window.open(file.url, '_blank')}
                              />
                              <Typography variant="caption" sx={{ p: 0.5, fontSize: '0.6rem' }}>
                                {file.originalName}
                              </Typography>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => moveQuestionUp(questionIndex)}
                    disabled={questionIndex === 0}
                  >
                    <ArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveQuestionDown(questionIndex)}
                    disabled={questionIndex === data.examinerQuestions.length - 1}
                  >
                    <ArrowDownIcon />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => editQuestion(questionIndex)}
                sx={publicSecondaryButtonSx}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteQuestion(questionIndex)}
                sx={{ ...publicSecondaryButtonSx, borderColor: 'rgba(239,68,68,0.24)', color: '#b42318' }}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Empty State */}
      {data.examinerQuestions.length === 0 && (
        <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No examiner questions added
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can add questions that students will answer after completing the history taking session,
            or skip this section entirely.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQuestion}
            sx={publicSecondaryButtonSx}
          >
            Add Your First Question
          </Button>
        </Paper>
      )}

      {/* Question Edit Dialog */}
      <Dialog
        open={questionDialog.open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {questionDialog.questionIndex === data.examinerQuestions.length ? 'Add New Question' : 'Edit Question'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Question"
                value={questionDialog.question?.question || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, question: e.target.value }
                })}
                helperText="The question that will be presented to students"
                required
                error={!questionDialog.question?.question?.trim()}
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Correct Answer"
                value={questionDialog.question?.correctAnswer || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, correctAnswer: e.target.value }
                })}
                helperText="The expected correct answer"
                required
                error={!questionDialog.question?.correctAnswer?.trim()}
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Marks"
                value={questionDialog.question?.maxMarks || 2}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, maxMarks: parseInt(e.target.value) || 0 }
                })}
                inputProps={{ min: 0, max: 10 }}
                required
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Marking Instructions"
                value={questionDialog.question?.markingInstructions || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, markingInstructions: e.target.value }
                })}
                helperText="Instructions for AI marking - how to award partial marks, common mistakes, etc."
                required
                error={!questionDialog.question?.markingInstructions?.trim()}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Media Files (Optional)
              </Typography>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="media-upload"
                multiple
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="media-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Images
                </Button>
              </label>

              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Supported formats: JPEG, PNG, GIF, WebP (max 10MB each)
              </Typography>

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {questionDialog.question?.mediaFiles && questionDialog.question.mediaFiles.length > 0 && (
                <Grid container spacing={2}>
                  {questionDialog.question.mediaFiles.map((file, fileIndex) => (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="150"
                          image={file.url}
                          alt={file.originalName}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" noWrap>
                            {file.originalName}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ p: 1, pt: 0 }}>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<RemoveIcon />}
                            onClick={() => removeFile(fileIndex)}
                          >
                            Remove
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={publicSecondaryButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={saveQuestion} 
            disabled={
              !questionDialog.question?.question?.trim() ||
              !questionDialog.question?.correctAnswer?.trim() ||
              !questionDialog.question?.markingInstructions?.trim()
            }
            sx={publicPrimaryButtonSx}
          >
            {questionDialog.questionIndex === data.examinerQuestions.length ? 'Add Question' : 'Save Question'}
          </Button>
        </DialogActions>
      </Dialog>
    </CaseEditorStepFrame>
  );
};

export default QuestionsStep;
